import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HostAppShell } from '../components/HostAppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { 
  Calendar, ChevronLeft, ChevronRight, Car, User,
  Lock, Unlock, CalendarDays, AlertCircle, CheckCircle,
  X, Plus, Wrench, Palmtree, Home, Ban, MoreHorizontal,
  Clock, Eye, Edit, Trash2, CalendarRange
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * HostAvailabilityPage - Manage availability across all vehicles
 * 
 * Features:
 * - Day Detail panel when tapping a date
 * - Block reasons (Maintenance, Holiday, Personal Use, Unavailable, Other)
 * - Per-vehicle status in Day Detail
 * - Fleet-safe multi-vehicle UX
 * - Mobile-first interaction
 */

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Block reason configuration
const BLOCK_REASONS = [
  { value: 'maintenance', label: 'Maintenance', icon: Wrench, color: 'amber' },
  { value: 'holiday', label: 'Holiday', icon: Palmtree, color: 'green' },
  { value: 'personal_use', label: 'Personal Use', icon: Home, color: 'purple' },
  { value: 'unavailable', label: 'Unavailable', icon: Ban, color: 'slate' },
  { value: 'other', label: 'Other', icon: MoreHorizontal, color: 'slate' }
];

const getReasonConfig = (reason) => {
  const config = BLOCK_REASONS.find(r => r.value === reason);
  if (config) return config;
  return { value: reason, label: 'Blocked', icon: Lock, color: 'red' };
};

// Vehicle status badge component
const VehicleStatusBadge = ({ status, statusLabel }) => {
  const configs = {
    available: { label: 'Available', className: 'bg-green-100 text-green-700 border-green-200' },
    booked: { label: 'Booked', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    maintenance: { label: 'Maintenance', className: 'bg-amber-100 text-amber-700 border-amber-200' },
    holiday: { label: 'Holiday', className: 'bg-green-100 text-green-700 border-green-200' },
    personal_use: { label: 'Personal Use', className: 'bg-purple-100 text-purple-700 border-purple-200' },
    unavailable: { label: 'Unavailable', className: 'bg-slate-100 text-slate-700 border-slate-200' },
    blocked: { label: statusLabel || 'Blocked', className: 'bg-red-100 text-red-700 border-red-200' }
  };
  const config = configs[status] || configs.blocked;
  return (
    <Badge className={`text-xs ${config.className}`}>
      {statusLabel || config.label}
    </Badge>
  );
};

// Day Detail Panel Component
const DayDetailPanel = ({ 
  date, 
  dayDetail, 
  loading, 
  onClose, 
  onBlock, 
  onUnblock, 
  onEditBlock,
  selectedVehicles,
  allVehicles 
}) => {
  if (!date) return null;
  
  const isPast = dayDetail?.is_past;
  const vehicleStatuses = dayDetail?.vehicle_statuses || [];
  
  // Filter to show only selected vehicles
  const displayStatuses = selectedVehicles.length > 0
    ? vehicleStatuses.filter(s => selectedVehicles.includes(s.vehicle_id))
    : vehicleStatuses;

  const formatBookingStatus = (status) => {
    const labels = {
      paid: 'Confirmed',
      active: 'In Progress',
      prehire_in_progress: 'Pre-Hire Check',
      prehire_submitted: 'Awaiting Approval',
      completed: 'Completed'
    };
    return labels[status] || status;
  };

  return (
    <Card className="border-slate-200 shadow-lg animate-in slide-in-from-bottom-2 lg:slide-in-from-right-2 duration-200">
      <CardHeader className="pb-2 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-semibold">{dayDetail?.display_date || date}</CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">
              {dayDetail?.summary?.available || 0} available • {dayDetail?.summary?.booked || 0} booked • {dayDetail?.summary?.blocked || 0} blocked
            </p>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="p-3 max-h-[60vh] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#004B8D]"></div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Vehicle Status List */}
            {displayStatuses.map((vs) => (
              <div 
                key={vs.vehicle_id}
                className="p-3 rounded-lg bg-slate-50 border border-slate-100"
                data-testid={`day-detail-vehicle-${vs.vehicle_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Car className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="font-medium text-sm truncate">{vs.vehicle_name}</span>
                  </div>
                  <VehicleStatusBadge status={vs.status} statusLabel={vs.status_label} />
                </div>
                
                {/* Booking Details */}
                {vs.status === 'booked' && vs.booking && (
                  <div className="mt-2 pt-2 border-t border-slate-200 space-y-1.5">
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <User className="w-3 h-3" />
                      <span>{vs.booking.renter_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <Calendar className="w-3 h-3" />
                      <span>{vs.booking.start_date} → {vs.booking.end_date}</span>
                    </div>
                    {vs.booking.pickup_time && (
                      <div className="flex items-center gap-2 text-xs text-slate-600">
                        <Clock className="w-3 h-3" />
                        <span>Pickup: {vs.booking.pickup_time}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>Ref: {vs.booking.booking_ref}</span>
                      <span className="text-slate-400">•</span>
                      <span>{formatBookingStatus(vs.booking.status)}</span>
                    </div>
                    <Link to={`/bookings/${vs.booking.booking_id}`}>
                      <Button variant="outline" size="sm" className="h-7 text-xs mt-2 w-full">
                        <Eye className="w-3 h-3 mr-1" />
                        View Booking
                      </Button>
                    </Link>
                  </div>
                )}
                
                {/* Block Details */}
                {vs.status !== 'booked' && vs.status !== 'available' && vs.block && (
                  <div className="mt-2 pt-2 border-t border-slate-200 space-y-1.5">
                    {vs.block.notes && (
                      <p className="text-xs text-slate-600">{vs.block.notes}</p>
                    )}
                    {vs.block.maintenance_details && (
                      <div className="text-xs text-slate-600 space-y-0.5">
                        {vs.block.maintenance_details.company && (
                          <p>Provider: {vs.block.maintenance_details.company}</p>
                        )}
                        {vs.block.maintenance_details.time && (
                          <p>Time: {vs.block.maintenance_details.time}</p>
                        )}
                      </div>
                    )}
                    {!isPast && (
                      <div className="flex gap-2 mt-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-7 text-xs flex-1"
                          onClick={() => onEditBlock(vs.vehicle_id, vs.block)}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => onUnblock(vs.vehicle_id)}
                        >
                          <Unlock className="w-3 h-3 mr-1" />
                          Unblock
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Available - Quick Block Action */}
                {vs.status === 'available' && !isPast && (
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-7 text-xs w-full"
                      onClick={() => onBlock(vs.vehicle_id)}
                    >
                      <Lock className="w-3 h-3 mr-1" />
                      Block this vehicle
                    </Button>
                  </div>
                )}
              </div>
            ))}
            
            {displayStatuses.length === 0 && (
              <div className="text-center py-6 text-slate-500 text-sm">
                No vehicles to display
              </div>
            )}
            
            {/* Bulk Actions */}
            {!isPast && displayStatuses.some(s => s.status === 'available') && (
              <div className="pt-3 border-t border-slate-100">
                <Button 
                  className="w-full bg-[#004B8D] hover:bg-[#003a6d] h-9"
                  onClick={() => onBlock(null)}
                  data-testid="block-all-vehicles-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Block {selectedVehicles.length > 0 ? 'Selected' : 'All'} Vehicles
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Block Modal Component
const BlockModal = ({ 
  open, 
  onClose, 
  date, 
  vehicleId, 
  vehicles,
  selectedVehicles,
  existingBlock,
  onSave,
  saving 
}) => {
  const [reason, setReason] = useState(existingBlock?.reason || 'unavailable');
  const [reasonLabel, setReasonLabel] = useState(existingBlock?.reason_label || '');
  const [notes, setNotes] = useState(existingBlock?.notes || '');
  const [maintenanceCompany, setMaintenanceCompany] = useState(existingBlock?.maintenance_details?.company || '');
  const [maintenanceTime, setMaintenanceTime] = useState(existingBlock?.maintenance_details?.time || '');
  const [applyToVehicles, setApplyToVehicles] = useState(
    vehicleId ? [vehicleId] : (selectedVehicles.length > 0 ? selectedVehicles : vehicles.map(v => v.vehicle_id))
  );
  const [rangeMode, setRangeMode] = useState(false);
  const [endDate, setEndDate] = useState('');

  const handleSave = () => {
    const data = {
      dates: rangeMode && endDate ? getDateRange(date, endDate) : [date],
      vehicle_ids: applyToVehicles.length > 0 ? applyToVehicles : null,
      reason,
      notes: notes || undefined,
      reason_label: reason === 'other' ? reasonLabel : undefined,
      maintenance_details: reason === 'maintenance' ? {
        company: maintenanceCompany || undefined,
        time: maintenanceTime || undefined
      } : undefined
    };
    onSave(data);
  };

  const getDateRange = (start, end) => {
    const dates = [];
    const current = new Date(start);
    const endDate = new Date(end);
    while (current <= endDate) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const formatDisplayDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const reasonConfig = getReasonConfig(reason);
  const ReasonIcon = reasonConfig.icon;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-slate-600" />
            {existingBlock ? 'Edit Block' : 'Block Date'}
          </DialogTitle>
          <DialogDescription>
            {formatDisplayDate(date)}
            {rangeMode && endDate && ` → ${formatDisplayDate(endDate)}`}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          {/* Range Mode Toggle */}
          {!existingBlock && (
            <div className="flex items-center gap-2">
              <Checkbox 
                id="range-mode"
                checked={rangeMode}
                onCheckedChange={setRangeMode}
              />
              <Label htmlFor="range-mode" className="text-sm">Block a date range</Label>
            </div>
          )}
          
          {rangeMode && (
            <div className="space-y-1.5">
              <Label className="text-sm">End Date</Label>
              <Input 
                type="date" 
                value={endDate}
                min={date}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          )}
          
          {/* Reason Selector */}
          <div className="space-y-1.5">
            <Label className="text-sm">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BLOCK_REASONS.map((r) => {
                  const Icon = r.icon;
                  return (
                    <SelectItem key={r.value} value={r.value}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        <span>{r.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          
          {/* Custom Reason Label (for Other) */}
          {reason === 'other' && (
            <div className="space-y-1.5">
              <Label className="text-sm">Custom Label</Label>
              <Input 
                value={reasonLabel}
                onChange={(e) => setReasonLabel(e.target.value)}
                placeholder="e.g., Inspection, Registration"
                maxLength={30}
              />
            </div>
          )}
          
          {/* Maintenance Details */}
          {reason === 'maintenance' && (
            <div className="space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-xs font-medium text-amber-800">Maintenance Details (optional)</p>
              <div className="space-y-1.5">
                <Label className="text-xs">Service Provider</Label>
                <Input 
                  value={maintenanceCompany}
                  onChange={(e) => setMaintenanceCompany(e.target.value)}
                  placeholder="e.g., Ultra Tune"
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Time</Label>
                <Input 
                  value={maintenanceTime}
                  onChange={(e) => setMaintenanceTime(e.target.value)}
                  placeholder="e.g., 9:30 AM"
                  className="h-8 text-sm"
                />
              </div>
            </div>
          )}
          
          {/* Notes */}
          <div className="space-y-1.5">
            <Label className="text-sm">Notes (optional)</Label>
            <Textarea 
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes..."
              rows={2}
              maxLength={200}
            />
          </div>
          
          {/* Vehicle Selector */}
          {!existingBlock && vehicles.length > 1 && (
            <div className="space-y-2">
              <Label className="text-sm">Apply to Vehicles</Label>
              <div className="space-y-1.5 max-h-32 overflow-y-auto p-2 bg-slate-50 rounded-lg">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox 
                    checked={applyToVehicles.length === vehicles.length}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setApplyToVehicles(vehicles.map(v => v.vehicle_id));
                      } else {
                        setApplyToVehicles([]);
                      }
                    }}
                  />
                  <span className="font-medium">All Vehicles</span>
                </label>
                {vehicles.map(v => (
                  <label key={v.vehicle_id} className="flex items-center gap-2 text-sm pl-4">
                    <Checkbox 
                      checked={applyToVehicles.includes(v.vehicle_id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setApplyToVehicles(prev => [...prev, v.vehicle_id]);
                        } else {
                          setApplyToVehicles(prev => prev.filter(id => id !== v.vehicle_id));
                        }
                      }}
                    />
                    <span className="truncate">{v.year} {v.make} {v.model}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSave}
            disabled={saving || (reason === 'other' && !reasonLabel)}
            className="bg-[#004B8D] hover:bg-[#003a6d]"
          >
            {saving ? 'Saving...' : existingBlock ? 'Update Block' : 'Block Date'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
const HostAvailabilityPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availability, setAvailability] = useState({
    vehicles: [],
    blocked_dates: {},
    upcoming_bookings: []
  });
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  
  // Day Detail state
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [dayDetailLoading, setDayDetailLoading] = useState(false);
  
  // Block Modal state
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockModalDate, setBlockModalDate] = useState(null);
  const [blockModalVehicleId, setBlockModalVehicleId] = useState(null);
  const [existingBlockData, setExistingBlockData] = useState(null);

  useEffect(() => {
    fetchAvailability();
  }, [currentMonth]);

  useEffect(() => {
    if (selectedDate) {
      fetchDayDetail(selectedDate);
    }
  }, [selectedDate, selectedVehicles]);

  const fetchAvailability = async () => {
    try {
      setLoading(true);
      const monthStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
      const res = await api.get(`/host/availability?month=${monthStr}`);
      setAvailability(res.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
      toast.error('Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const fetchDayDetail = async (date) => {
    try {
      setDayDetailLoading(true);
      const vehicleParam = selectedVehicles.length === 1 ? `&vehicle_id=${selectedVehicles[0]}` : '';
      const res = await api.get(`/host/availability/day/${date}?${vehicleParam}`);
      setDayDetail(res.data);
    } catch (error) {
      console.error('Error fetching day detail:', error);
      toast.error('Failed to load day details');
    } finally {
      setDayDetailLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
    setSelectedDate(null);
    setDayDetail(null);
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push({ day: null, date: null });
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push({ day, date });
    }
    return days;
  };

  const getDateStatus = (date) => {
    if (!date) return { status: 'empty' };
    
    const blocks = availability.blocked_dates?.[date] || [];
    const booking = availability.upcoming_bookings?.find(b => 
      date >= b.start_date && date <= b.end_date
    );
    
    // Filter blocks/bookings by selected vehicles
    const relevantBlocks = selectedVehicles.length > 0
      ? blocks.filter(b => selectedVehicles.includes(b.vehicle_id))
      : blocks;
    
    const relevantBooking = selectedVehicles.length > 0
      ? (booking && selectedVehicles.includes(booking.vehicle_id) ? booking : null)
      : booking;
    
    // P0 FIX: Priority order - bookings take precedence over blocks
    if (relevantBooking) {
      return { status: 'booked', booking: relevantBooking };
    }
    
    // P0 FIX: Check if blocks are booking-related (have booking_id) vs manual blocks
    if (relevantBlocks.length > 0) {
      // Separate booking blocks from manual blocks
      const bookingBlocks = relevantBlocks.filter(b => b.booking_id);
      const manualBlocks = relevantBlocks.filter(b => !b.booking_id);
      
      // If there are booking blocks but no matching booking in upcoming_bookings,
      // this could be a past/completed booking - show as historical booked (blue/grey)
      if (bookingBlocks.length > 0 && manualBlocks.length === 0) {
        // These are booking-related blocks - show as "booked" not "blocked"
        return { status: 'booked', booking: { booking_id: bookingBlocks[0].booking_id, status: 'historical' } };
      }
      
      // If there are manual blocks, determine primary block reason
      if (manualBlocks.length > 0) {
        const reasons = manualBlocks.map(b => b.reason);
        const primaryReason = reasons.includes('maintenance') ? 'maintenance' :
                             reasons.includes('holiday') ? 'holiday' :
                             reasons.includes('personal_use') ? 'personal_use' :
                             reasons.includes('booking_cancelled') ? 'blocked' :  // Cancelled booking blocks
                             'blocked';
        return { status: 'blocked', reason: primaryReason, blocks: manualBlocks };
      }
      
      // Fallback for booking blocks without matching booking
      return { status: 'booked', booking: { booking_id: bookingBlocks[0]?.booking_id, status: 'historical' } };
    }
    
    return { status: 'available' };
  };

  const handleDateClick = (date) => {
    if (!date) return;
    setSelectedDate(date);
  };

  const handleBlock = (vehicleId) => {
    setBlockModalDate(selectedDate);
    setBlockModalVehicleId(vehicleId);
    setExistingBlockData(null);
    setBlockModalOpen(true);
  };

  const handleEditBlock = (vehicleId, block) => {
    setBlockModalDate(selectedDate);
    setBlockModalVehicleId(vehicleId);
    setExistingBlockData(block);
    setBlockModalOpen(true);
  };

  const handleUnblock = async (vehicleId) => {
    setSaving(true);
    try {
      await api.post('/host/availability/unblock', {
        dates: [selectedDate],
        vehicle_ids: vehicleId ? [vehicleId] : (selectedVehicles.length > 0 ? selectedVehicles : null)
      });
      toast.success('Date unblocked');
      fetchAvailability();
      fetchDayDetail(selectedDate);
    } catch (error) {
      console.error('Error unblocking:', error);
      toast.error('Failed to unblock date');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBlock = async (data) => {
    setSaving(true);
    try {
      await api.post('/host/availability/block', data);
      toast.success(`Blocked ${data.dates.length} date(s)`);
      setBlockModalOpen(false);
      fetchAvailability();
      if (selectedDate) {
        fetchDayDetail(selectedDate);
      }
    } catch (error) {
      console.error('Error blocking:', error);
      toast.error('Failed to block date');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short'
    });
  };

  const days = getDaysInMonth();
  const today = new Date().toISOString().split('T')[0];

  return (
    <HostAppShell title="Availability" activeId="calendar">
      <div className="max-w-6xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-heading font-semibold text-slate-900">Fleet Availability</h2>
            <p className="text-sm text-slate-500">Tap a date to view details and manage blocks</p>
          </div>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004B8D]"></div>
          </div>
        ) : availability.vehicles?.length === 0 ? (
          /* P2 FIX: Empty state when host has no vehicles */
          <Card className="border-slate-100">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <Car className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                Add Your First Vehicle
              </h3>
              <p className="text-sm text-slate-500 max-w-sm mb-6">
                List a vehicle to start managing your availability calendar. 
                You'll be able to block dates, track bookings, and manage your fleet from here.
              </p>
              <Button 
                onClick={() => navigate('/host/vehicles/add')}
                className="bg-[#004B8D] hover:bg-[#003a6d]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Vehicle
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Calendar Column */}
            <div className="lg:col-span-2 space-y-4">
              {/* Calendar Card */}
              <Card className="border-slate-100">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)}>
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <h3 className="font-heading font-semibold text-lg">
                      {currentMonth.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
                    </h3>
                    <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)}>
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Day headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {DAYS_OF_WEEK.map(day => (
                      <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {days.map((dayObj, idx) => {
                      const { day, date } = dayObj;
                      if (!day) {
                        return <div key={idx} className="aspect-square" />;
                      }
                      
                      const dateStatus = getDateStatus(date);
                      const isSelected = selectedDate === date;
                      const isPast = date < today;
                      const isToday = date === today;
                      
                      // Get visual config based on status
                      const getStatusStyle = () => {
                        if (dateStatus.status === 'booked') {
                          return 'bg-blue-100 text-blue-700';
                        }
                        if (dateStatus.status === 'blocked') {
                          if (dateStatus.reason === 'maintenance') return 'bg-amber-100 text-amber-700';
                          if (dateStatus.reason === 'holiday') return 'bg-green-100 text-green-700';
                          if (dateStatus.reason === 'personal_use') return 'bg-purple-100 text-purple-700';
                          return 'bg-red-100 text-red-700';
                        }
                        return '';
                      };
                      
                      const getStatusIcon = () => {
                        if (dateStatus.status === 'booked') {
                          return <Car className="w-3 h-3 text-blue-500" />;
                        }
                        if (dateStatus.status === 'blocked') {
                          if (dateStatus.reason === 'maintenance') return <Wrench className="w-3 h-3 text-amber-500" />;
                          if (dateStatus.reason === 'holiday') return <Palmtree className="w-3 h-3 text-green-500" />;
                          if (dateStatus.reason === 'personal_use') return <Home className="w-3 h-3 text-purple-500" />;
                          return <Lock className="w-3 h-3 text-red-500" />;
                        }
                        return null;
                      };
                      
                      return (
                        <button
                          key={idx}
                          onClick={() => handleDateClick(date)}
                          className={`
                            aspect-square rounded-lg text-sm font-medium relative
                            transition-all duration-150
                            ${isPast ? 'text-slate-300 cursor-default' : 'cursor-pointer hover:ring-2 hover:ring-slate-200'}
                            ${isSelected ? 'ring-2 ring-[#004B8D] ring-offset-1' : ''}
                            ${isToday && !isSelected ? 'ring-2 ring-slate-200' : ''}
                            ${getStatusStyle()}
                          `}
                          data-testid={`calendar-day-${date}`}
                        >
                          {day}
                          {getStatusIcon() && (
                            <div className="absolute bottom-0.5 right-0.5">
                              {getStatusIcon()}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  
                  {/* Legend */}
                  <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 rounded bg-blue-100 flex items-center justify-center">
                        <Car className="w-2.5 h-2.5 text-blue-500" />
                      </div>
                      <span>Booked</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 rounded bg-amber-100 flex items-center justify-center">
                        <Wrench className="w-2.5 h-2.5 text-amber-500" />
                      </div>
                      <span>Maintenance</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 rounded bg-green-100 flex items-center justify-center">
                        <Palmtree className="w-2.5 h-2.5 text-green-500" />
                      </div>
                      <span>Holiday</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 rounded bg-red-100 flex items-center justify-center">
                        <Lock className="w-2.5 h-2.5 text-red-500" />
                      </div>
                      <span>Blocked</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Day Detail Panel - Mobile (below calendar) */}
              <div className="lg:hidden">
                {selectedDate && (
                  <DayDetailPanel
                    date={selectedDate}
                    dayDetail={dayDetail}
                    loading={dayDetailLoading}
                    onClose={() => { setSelectedDate(null); setDayDetail(null); }}
                    onBlock={handleBlock}
                    onUnblock={handleUnblock}
                    onEditBlock={handleEditBlock}
                    selectedVehicles={selectedVehicles}
                    allVehicles={availability.vehicles}
                  />
                )}
              </div>
            </div>
            
            {/* Right Sidebar */}
            <div className="space-y-4">
              {/* Vehicle Filter */}
              <Card className="border-slate-100">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Filter by Vehicle</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox 
                        checked={selectedVehicles.length === 0}
                        onCheckedChange={(checked) => {
                          if (checked) setSelectedVehicles([]);
                        }}
                      />
                      <span className="font-medium">All Vehicles</span>
                    </label>
                    {availability.vehicles?.map(v => (
                      <label key={v.vehicle_id} className="flex items-center gap-2 text-sm">
                        <Checkbox 
                          checked={selectedVehicles.includes(v.vehicle_id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedVehicles(prev => [...prev, v.vehicle_id]);
                            } else {
                              setSelectedVehicles(prev => prev.filter(id => id !== v.vehicle_id));
                            }
                          }}
                        />
                        <span className="truncate">{v.year} {v.make} {v.model}</span>
                      </label>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {/* Day Detail Panel - Desktop (sidebar) */}
              <div className="hidden lg:block">
                {selectedDate ? (
                  <DayDetailPanel
                    date={selectedDate}
                    dayDetail={dayDetail}
                    loading={dayDetailLoading}
                    onClose={() => { setSelectedDate(null); setDayDetail(null); }}
                    onBlock={handleBlock}
                    onUnblock={handleUnblock}
                    onEditBlock={handleEditBlock}
                    selectedVehicles={selectedVehicles}
                    allVehicles={availability.vehicles}
                  />
                ) : (
                  <Card className="border-slate-100 border-dashed">
                    <CardContent className="py-8 text-center">
                      <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500">Tap a date to see details</p>
                    </CardContent>
                  </Card>
                )}
              </div>
              
              {/* Upcoming Bookings */}
              <Card className="border-slate-100">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Upcoming Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  {availability.upcoming_bookings?.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">
                      No upcoming bookings
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {availability.upcoming_bookings?.slice(0, 5).map(booking => (
                        <Link 
                          key={booking.booking_id}
                          to={`/bookings/${booking.booking_id}`}
                          className="block p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                        >
                          <p className="text-sm font-medium text-slate-900 truncate">{booking.vehicle_name}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                            <Calendar className="w-3 h-3" />
                            <span>{formatDate(booking.start_date)} - {formatDate(booking.end_date)}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Quick Stats */}
              <Card className="border-slate-100">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Blocked Days</span>
                    <Badge variant="outline">{availability.total_blocked_days || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-2">
                    <span className="text-slate-500">Upcoming Trips</span>
                    <Badge variant="outline">{availability.upcoming_bookings?.length || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-2">
                    <span className="text-slate-500">Active Vehicles</span>
                    <Badge variant="outline">{availability.vehicles?.length || 0}</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
      
      {/* Block Modal */}
      <BlockModal
        open={blockModalOpen}
        onClose={() => setBlockModalOpen(false)}
        date={blockModalDate}
        vehicleId={blockModalVehicleId}
        vehicles={availability.vehicles || []}
        selectedVehicles={selectedVehicles}
        existingBlock={existingBlockData}
        onSave={handleSaveBlock}
        saving={saving}
      />
    </HostAppShell>
  );
};

export default HostAvailabilityPage;
